import type {
  AnnouncementOrganizationStatusValue,
  AnnouncementStatus,
  AnnouncementType
} from '@/api/announcements-client'

export const DEFAULT_ANNOUNCEMENT_LINK_URL = 'https://trust.epilot.cloud'
export const DEFAULT_ANNOUNCEMENT_WINDOW_DAYS = 14

export const ANNOUNCEMENT_TYPE_LABELS: Record<AnnouncementType, string> = {
  subprocessor_change: 'Subprocessor change'
}

export const ANNOUNCEMENT_STATUS_LABELS: Record<AnnouncementStatus, string> = {
  draft: 'Draft',
  published: 'Published',
  archived: 'Archived'
}

export const ORGANIZATION_STATUS_LABELS: Record<
  AnnouncementOrganizationStatusValue,
  string
> = {
  acknowledged: 'Acknowledged',
  pending: 'Pending',
  expired: 'Expired',
  superseded: 'Superseded'
}

/** Banner copy shown in the 360 portal (German, informal "du"). */
export const BANNER_COPY = {
  acknowledgeButton: 'Zur Kenntnis genommen',
  link: 'Zur Liste',
  nonAdminHint:
    'Ein Administrator deiner Organisation kann die Änderung bestätigen.'
}

const ISO_DATE_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * Formats a `YYYY-MM-DD` date as German `TT.MM.JJJJ` without going through `Date`
 * (avoids timezone shifts). Returns the placeholder when the input is empty/invalid.
 */
export const formatGermanDate = (
  isoDate?: string,
  placeholder = 'TT.MM.JJJJ'
) => {
  const match = ISO_DATE_REGEX.exec(isoDate ?? '')

  if (!match) {
    return placeholder
  }

  const [, year, month, day] = match

  return `${day}.${month}.${year}`
}

export const buildDefaultGermanMessage = (effectiveDate?: string) =>
  `Wir haben unsere Liste der Subdienstleister aktualisiert (gültig ab ${formatGermanDate(
    effectiveDate
  )}). Bitte prüfe die Änderungen.`

/**
 * The DE message follows the effective date as long as it has not been edited manually,
 * i.e. while it is empty or still equals the template generated for the previous date.
 */
export const shouldUpdateGermanMessage = (
  currentMessage: string | undefined,
  previousEffectiveDate: string | undefined
) =>
  !currentMessage?.trim() ||
  currentMessage === buildDefaultGermanMessage(previousEffectiveDate)

export const buildDefaultEnglishMessage = (effectiveDate?: string) =>
  `We have updated our list of subprocessors (effective ${formatGermanDate(
    effectiveDate,
    'DD.MM.YYYY'
  )}). Please review the changes.`

/** Same as {@link shouldUpdateGermanMessage} for the EN message. */
export const shouldUpdateEnglishMessage = (
  currentMessage: string | undefined,
  previousEffectiveDate: string | undefined
) =>
  !currentMessage?.trim() ||
  currentMessage === buildDefaultEnglishMessage(previousEffectiveDate)

/** Formats an ISO timestamp as `dd.MM.yyyy HH:mm` (local time), `–` when empty/invalid. */
export const formatDateTime = (isoDateTime?: string) => {
  if (!isoDateTime) {
    return '–'
  }

  const date = new Date(isoDateTime)

  if (Number.isNaN(date.getTime())) {
    return '–'
  }

  const pad = (n: number) => String(n).padStart(2, '0')

  return `${pad(date.getDate())}.${pad(
    date.getMonth() + 1
  )}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export const buildCsvFilename = (
  listVersion: string | undefined,
  status: string | undefined,
  now = new Date()
) => {
  const safe = (value: string) => value.replace(/[^a-zA-Z0-9._-]+/g, '-')

  return (
    [
      'subprocessor-announcement',
      safe(listVersion || 'unknown'),
      status ? safe(status) : 'all',
      now.toISOString().slice(0, 10)
    ].join('_') + '.csv'
  )
}

export const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')

  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

export const getErrorMessage = (error: unknown) => {
  const err = error as {
    response?: { data?: { message?: string; error?: string } }
    message?: string
  }

  return (
    err?.response?.data?.message ??
    err?.response?.data?.error ??
    err?.message ??
    'An unexpected error occurred.'
  )
}
